import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Loader,
  Users
} from 'lucide-react';
import { adminAPI } from '../services/api';
import axios from 'axios';
import type { Organization, ListQueryParams, PaginatedResponse } from '../types';
import { theme } from '../styles/theme';
import { Chip } from '../components/Chip';
import { MetricTile, MetricTileGrid } from '../components/MetricTile';
import { extractApiError } from '../utils/errorMessages';
import { useToast } from '../hooks/use-toast';
import { useConfirm } from '../contexts/ConfirmContext';

const AdminOrganizations: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();

  // Redirect non-admin users
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  // State
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  
  // Stats
  const [stats, setStats] = useState({
    total_organizations: 0,
    active_organizations: 0,
    inactive_organizations: 0,
    total_users_in_organizations: 0,
  });

  // Form state for creating/editing organization
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  // Load organizations
  const loadOrganizations = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params: ListQueryParams = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await adminAPI.organizations.list(params);
      // Handle paginated response from DRF
      const orgData = (response.data as unknown as PaginatedResponse<Organization>).results ?? (response.data as Organization[]);
      setOrganizations(Array.isArray(orgData) ? orgData : []);
    } catch (error) {
      console.error('Failed to load organizations:', error);
      toast({ title: extractApiError(error, "Couldn't load organisations"), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await adminAPI.organizations.stats();
      const data = response.data as typeof stats & {
        organizations?: Array<{ user_count?: number }>;
      };

      // The endpoint returns total/active/inactive plus a per-organisation
      // list; it has never returned `total_users_in_organizations`, so the
      // "Total Users" tile read an undefined key and rendered blank. The
      // figure is derivable from the list the same response already carries,
      // which is cheaper than adding a field to the API for one tile.
      setStats({
        ...data,
        total_users_in_organizations:
          data.total_users_in_organizations ??
          (data.organizations ?? []).reduce((sum, o) => sum + (o.user_count ?? 0), 0),
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadOrganizations();
    loadStats();
  }, [searchQuery, statusFilter]);

  // Create organization
  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminAPI.organizations.create(formData);
      toast({ title: `${formData.name} created`, variant: 'success' });
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
      loadOrganizations();
      loadStats();
    } catch (error: unknown) {
      console.error('Failed to create organization:', error);
      let errorMsg = 'Failed to create organization';
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as Record<string, unknown> | undefined;
        errorMsg = (data?.name as string[] | undefined)?.[0]
          ?? (data?.detail as string | undefined)
          ?? (data ? JSON.stringify(data) : undefined)
          ?? error.message
          ?? 'Failed to create organization';
      }
      toast({ title: errorMsg, variant: 'error' });
    }
  };

  // Update organization
  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;

    try {
      await adminAPI.organizations.update(selectedOrg.id, formData);
      toast({ title: `${formData.name} saved`, variant: 'success' });
      setShowEditModal(false);
      setSelectedOrg(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
      loadOrganizations();
      loadStats();
    } catch (error: unknown) {
      console.error('Failed to update organization:', error);
      let errorMsg = 'Failed to update organization';
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as Record<string, unknown> | undefined;
        errorMsg = (data?.name as string[] | undefined)?.[0]
          ?? (data?.detail as string | undefined)
          ?? error.message
          ?? 'Failed to update organization';
      }
      toast({ title: errorMsg, variant: 'error' });
    }
  };

  // The prompt names the outcome rather than asking about "toggling a status",
  // so the question and the button agree about what will happen.
  const handleToggleActive = async (org: Organization) => {
    const deactivating = org.is_active;
    const ok = await confirm({
      title: deactivating ? `Deactivate ${org.name}?` : `Reactivate ${org.name}?`,
      description: deactivating ? 'Its members will not be able to sign in.' : undefined,
      confirmText: deactivating ? 'Deactivate' : 'Reactivate',
    });
    if (!ok) return;

    try {
      await adminAPI.organizations.toggleActive(org.id);
      loadOrganizations(true);
      loadStats();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't change that organisation"), variant: 'error' });
    }
  };

  const handleDeleteOrganization = async (orgId: string, orgName: string) => {
    const ok = await confirm({
      title: `Delete ${orgName}?`,
      description: 'This cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      await adminAPI.organizations.delete(orgId);
      toast({ title: `${orgName} deleted`, variant: 'success' });
      loadOrganizations(true);
      loadStats();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't delete that organisation"), variant: 'error' });
    }
  };

  // Open edit modal
  const openEditModal = (org: Organization) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      description: org.description || '',
      is_active: org.is_active,
    });
    setShowEditModal(true);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <Link 
        to="/admin" 
        className={`inline-flex items-center ${theme.text.secondary} hover:text-interactive-primary-hover mb-4 transition`}
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Admin Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="sr-only">Organization Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create Organization</span>
        </button>
      </div>

      {/* Stats */}
      <MetricTileGrid columns={4} className="mb-6">
        <MetricTile label="Total Organizations" value={stats.total_organizations} IconComponent={Building2} />
        <MetricTile label="Active" value={stats.active_organizations} IconComponent={CheckCircle} />
        <MetricTile label="Inactive" value={stats.inactive_organizations} IconComponent={XCircle} />
        <MetricTile label="Total Users" value={stats.total_users_in_organizations} IconComponent={Users} />
</MetricTileGrid>

      {/* Filters */}
      <div className={`${theme.card.background} rounded-lg shadow p-6 mb-6`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-text-tertiary" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      {loading ? (
        <div className={`${theme.card.background} rounded-lg shadow p-12 text-center`}>
          <Loader className="w-12 h-12 text-interactive-primary animate-spin mx-auto mb-4" />
          <p className={`${theme.text.secondary}`}>Loading organizations...</p>
        </div>
      ) : (
        <div className={`${theme.card.background} rounded-lg shadow overflow-hidden`}>
          <table className="w-full">
            <thead className="bg-bg-subtle">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme.border.primary.replace('border-', 'divide-')}`}>
              {organizations.map((org) => (
                <tr key={org.id} className="hover:bg-bg-subtle">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-bg-subtle border border-border flex items-center justify-center text-text-primary font-semibold">
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${theme.text.primary}`}>
                          {org.name}
                        </div>
                        <div className="text-sm text-text-tertiary">
                          Created {new Date(org.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.text.primary} max-w-xs truncate`}>
                    {org.description || <span className="text-text-tertiary">No description</span>}
                  </td>
                  <td className="px-6 py-4">
                    <Chip icon={<Users className="w-3 h-3" aria-hidden="true" />}>{org.user_count || 0}</Chip>
                  </td>
                  <td className="px-6 py-4">
                    {org.is_active ? (
                      <Chip icon={<CheckCircle className="w-3 h-3" aria-hidden="true" />}>Active</Chip>
                    ) : (
                      <Chip tone="muted" icon={<XCircle className="w-3 h-3" aria-hidden="true" />}>Inactive</Chip>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openEditModal(org)}
                        className="p-2 text-text-secondary hover:text-interactive-primary-hover"
                        title="Edit Organization"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(org)}
                        className="p-2 text-text-secondary hover:text-qc-success"
                        title="Toggle Active Status"
                      >
                        {org.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteOrganization(org.id, org.name)}
                        className="p-2 text-text-secondary hover:text-qc-error"
                        title="Delete Organization"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-8 max-w-md w-full mx-4`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
              <Building2 className="w-6 h-6 mr-2" />
              Create New Organization
            </h2>
            <form onSubmit={handleCreateOrganization}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                    placeholder="Enter organization name"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                    rows={3}
                    placeholder="Enter organization description (optional)"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_create"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-interactive-primary border-border rounded focus:ring-interactive-primary"
                  />
                  <label htmlFor="is_active_create" className={`ml-2 text-sm ${theme.text.secondary}`}>
                    Organization is active
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '', is_active: true });
                  }}
                  className={`px-4 py-2 ${theme.text.secondary} hover:bg-bg-subtle rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg"
                >
                  Create Organization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && selectedOrg && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-8 max-w-md w-full mx-4`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
              <Edit2 className="w-6 h-6 mr-2" />
              Edit Organization
            </h2>
            <form onSubmit={handleUpdateOrganization}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                    rows={3}
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_edit"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-interactive-primary border-border rounded focus:ring-interactive-primary"
                  />
                  <label htmlFor="is_active_edit" className={`ml-2 text-sm ${theme.text.secondary}`}>
                    Organization is active
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedOrg(null);
                    setFormData({ name: '', description: '', is_active: true });
                  }}
                  className={`px-4 py-2 ${theme.text.secondary} hover:bg-bg-subtle rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg"
                >
                  Update Organization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrganizations;
